import React, { createContext, useContext } from 'react';
import {
  CannedErrorPage,
  FiveHundredErrorPage,
  FourHundredErrorPage,
  FourOhFourErrorPage,
  FourTenErrorPage,
} from '~app/components/Errors';
import GoToURL from '~app/components/Survey/GoToURL';
import { UseSurveyErrorsReturn } from './types';

const SurveyErrorsContext = createContext<UseSurveyErrorsReturn>({
  errors: [],
  errorGroups: {},
  hasNoErrors: true,
  hasVersionChanged: false,
  hasNotFoundErrors: false,
  hasValidationErrors: false,
  hasRedirectErrors: false,
  hasGoneErrors: false,
  hasCannedError: false,
  hasBadRequestErrors: false,
  hasUnknownErrors: false,
  reset: () => {},
  setError: () => {},
  setErrors: () => {},
  setErrorsById: () => {},
  getErrorMetaByType: () => ({}),
  getErrorsByType: () => [],
  getErrorsById: () => [],
  removeErrorsByType: () => {},
  removeErrorsById: () => {},
  setMeta: () => {},
});

export function useSurveyErrorsContext(): UseSurveyErrorsReturn {
  return useContext(SurveyErrorsContext);
}

function SurveyErrorsProvider({
  children,
  ...api
}: { children: React.ReactNode } & UseSurveyErrorsReturn): React.ReactElement {
  if (api.hasNotFoundErrors) {
    return <FourOhFourErrorPage />;
  }

  if (api.hasRedirectErrors) {
    const { url } = api.getErrorMetaByType(api.errorGroups.redirect);
    // Should this show an interim page for redirection with this component?
    // or, if not, this could become a pure function.
    return <GoToURL url={url} />;
  }

  if (api.hasCannedError) {
    return <CannedErrorPage />;
  }

  if (api.hasGoneErrors) {
    return <FourTenErrorPage />;
  }

  if (api.hasBadRequestErrors) {
    return <FourHundredErrorPage />;
  }

  if (api.hasUnknownErrors) {
    return <FiveHundredErrorPage />;
  }

  return (
    <SurveyErrorsContext.Provider value={api}>
      <>{children}</>
    </SurveyErrorsContext.Provider>
  );
}

export default SurveyErrorsProvider;
